// Dealer-stock analytics: the staff reports (/analytics/dealer/* on the
// auction API, admin only) and the public beacon this portal's dealer-stock
// pages send when a car is opened or a search is applied.

import axiosInterceptors from "./axiosHandler";
import { baseURL } from "./index";

// One of overview | demand | searches | listings | leads | customers | sales.
// `range` is "days=30" or "from=YYYY-MM-DD&to=YYYY-MM-DD" (US Eastern days).
export const getDealerReport = (report, range) =>
  axiosInterceptors().get(`${baseURL}/analytics/dealer/${report}?${range}`);

// Every report in one markdown file, with no personal data in it: for pasting
// into Claude or saving for a planning session.
export const getDealerDigestMarkdown = (range) =>
  axiosInterceptors().get(`${baseURL}/analytics/dealer/digest?${range}&format=md`, { responseType: "text" });

// ---- Beacon ----

const storedToken = () => {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch (err) {
    return null;
  }
};

// Fire and forget. The API answers 204 whatever happened and drops repeats
// within half an hour, so nothing here retries or waits; a failure must never
// reach the page.
const sendDealerEvent = (payload) => {
  try {
    const headers = { "Content-Type": "application/json" };
    const token = storedToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${baseURL}/analytics/dealer/events`, {
      method: "POST",
      headers,
      credentials: "include",
      keepalive: true,
      body: JSON.stringify({ origin: "portal", path: window.location.pathname, ...payload }),
    }).catch(() => {});
  } catch (err) {
    // Analytics never breaks a page.
  }
};

const text = (value, max) => (value === null || value === undefined ? "" : String(value).slice(0, max));
const yearOf = (value) => {
  const n = parseInt(value, 10);
  return n >= 1900 && n <= 2100 ? n : null;
};

/** A dealer-stock car page was opened. `usdPrice` is the price the page shows. */
export const trackDealerView = (vehicle, usdPrice) => {
  if (!vehicle || !/^[0-9a-f]{24}$/i.test(String(vehicle._id || ""))) return;
  const price = Number(usdPrice);
  sendDealerEvent({
    type: "view",
    vehicleId: String(vehicle._id),
    vehicle: {
      source: vehicle.source === "current-inventory" ? "current-inventory" : "dealer-stock",
      title: text(vehicle.title || [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" "), 200),
      year: yearOf(vehicle.year),
      make: text(vehicle.make, 100),
      model: text(vehicle.model, 100),
      usdPrice: Number.isFinite(price) && price > 0 ? Math.round(price) : null,
    },
  });
};

/**
 * A search or filter was applied on the dealer-stock grid. `filters` is the
 * applied filter object; only the NAMES of the filters set are sent, plus the
 * make and model when one is chosen.
 */
export const trackDealerSearch = ({ query, filters = {}, resultCount }) => {
  const set = Object.keys(filters).filter((key) => {
    const v = filters[key];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  });
  const first = (v) => (Array.isArray(v) ? v[0] : v);
  if (!String(query || "").trim() && !set.length) return;
  sendDealerEvent({
    type: "search",
    search: {
      query: text(query, 120).trim(),
      filters: set.slice(0, 20).map((k) => k.slice(0, 64)),
      make: text(first(filters.make), 100),
      model: text(first(filters.model), 100),
      resultCount: Number.isInteger(resultCount) ? resultCount : null,
    },
  });
};
